import { Component, OnInit } from '@angular/core';
import { SubscriptionService } from '../../subscription.service';
import {Router, ActivatedRoute} from '@angular/router';
import { LoaderService } from '../../../loader.service';
import { SharedService } from '../../../shared/shared.service';
import { AuthService } from '../../../auth.service';
import { ConfirmDialogService } from '../../../shared/confirm-dialog/confirm-dialog.service';
import { AlertDialogOptions, AlertDialogService } from '../../../shared/alert-dialog/alert-dialog.service';
import { subscriptionOperationFailureAlert, subscriptionUpdateSuccessAlert } from '../../subscription.create-alert';
import { AuthzAccessHintService } from '../../../shared/authz-access-hint/authz-access-hint.service';
import { AUTHZ_FEATURES } from '../../../constants/permissions';
import { permissionsForFeature } from '../../../utils/permission.utils';


interface UserSelectorDetails {
    name: string;
}

@Component({
    selector:"app-view-accessfee-plan",
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.css']
})

export class ViewAccessFeeComponent implements OnInit{
    public planData: any = {};
    public canDeactivate = false;
    public authzAccessHint: { message: string; requestUrl: string } | null = null;

    constructor(
        private subscriptionService :SubscriptionService,
        private route: ActivatedRoute,
        private loaderService: LoaderService,
        private sharedService: SharedService,
        private router: Router,
        private accessRights: AuthService,
        private dialogService: ConfirmDialogService,
        private alertDialogService: AlertDialogService,
        private authzAccessHintService: AuthzAccessHintService,
    ){}

    private cityMap = {};
    public cityIdNameMap = {};
    public serviceMap = {};
    public userSelectors: string[] = [];
    public zones: string[] = [];
    public services: string[] = [];


    get showDeactivate() {
        return this.canDeactivate && this.planData && this.planData.active;
    }

    async ngOnInit(){
        const editPermission = (permissionsForFeature(AUTHZ_FEATURES.BILLING_PLANS, this.accessRights.getAuthzPanels()) || {}).edit;
        this.canDeactivate = this.accessRights.isAuthorizedFor(editPermission, AUTHZ_FEATURES.BILLING_PLANS);
        this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('billingPlans');
        await this.withLoader(async () => {
            await this.fetchAllCities();
            const planId = this.route.snapshot.paramMap.get('id');
            try {
                const res: any = await this.subscriptionService.getBillingPlan(planId).toPromise();
                this.planData = res;
                let cityId = res.city;
    
                this.planData.city = this.cityIdNameMap[cityId] || "NA";
    
                const serviceIdToNameMap = {};
    
                await this.sharedService.fetchServices(cityId)
                                        .toPromise()
                                        .then((result: any) => {
                                            return result.data.map(
                                                sd => {
                                                    if (sd && sd.service && sd.service._id) {
                                                      serviceIdToNameMap[sd.service._id] = { name: sd.service.name };
                                                    }
                                                  }
                                            );
                                        });
    
                this.planData.services = this.planData.services.map(serviceId => serviceIdToNameMap[serviceId].name).join(',');
            
                if(this.planData.userSelectors) {
                    const userSelectorDetails = await Promise.all(
                            this.planData.userSelectors.map(selector => 
                                this.sharedService.getUserSelectorDetails(selector).toPromise()
                            )
                        );
                    this.userSelectors = userSelectorDetails.map((response: UserSelectorDetails) => response.name);
                    this.planData.userSelectors = this.userSelectors.join(',');
                }
    
                if (this.planData.zones) {
                    let zonesIds = this.planData.zones;
    
                    const zonesDetails = await this.sharedService.fetchBulkZones(zonesIds)
                        .toPromise()
                        .then((result: any) => {
                            return result.geolayers.map((layer: any) => layer.name).join(',');
                        });
    
                    this.planData.zones = zonesDetails; 
                }
              } catch (err) {
                console.log(err);
              }
        })
    }

    async withLoader(cb) {
        try {
          this.loaderService.openLoading();
          return await cb();
        } finally {
          this.loaderService.closeLoading()
        }
    }

    async fetchAllCities() {
        const citiesRes: any = await this.sharedService.fetchCities().toPromise();
        const citiesData =citiesRes.data.cities;
        for (const city of citiesData) {
            this.cityMap[city.displayName] = city._id;
            this.cityIdNameMap[city._id] = city.displayName;
        }
    }

    private showAlertBox(options: AlertDialogOptions) {
        this.alertDialogService.open(options);
    }

    async deactivateAccessFeeRule() {
        const options = {
            title: `Are you sure to deactivate the ${this.planData.planName} plan ?`,
            message: `You are about to deactivate accessfee ${this.planData.planName}`,
            cancelText: 'Cancel',
            confirmText: 'Yes, Proceed'
        };
        this.dialogService.open(options);
        this.dialogService.confirmed().subscribe(async (confirmed) => {
            if (!confirmed) {
                return;
            }
            try {
                const resp = await this.subscriptionService.updateBillingPlan(
                  this.planData.id || this.route.snapshot.paramMap.get('id'),
                  { active: false }
                ).toPromise();
                this.planData.active = false;
                this.showAlertBox(subscriptionUpdateSuccessAlert(resp));
            } catch (error) {
                this.showAlertBox(subscriptionOperationFailureAlert(error));
            }
        });
    }
}
